import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../AxiosInstance';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import TimeInput from './TimeInput';

const HHMM_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const parseHHMM = (str) => {
  if (!str || !HHMM_REGEX.test(str.trim())) return null;
  const [h, m] = str.trim().split(':').map(Number);
  return h * 60 + m;
};

/**
 * Add New Availability (Instructor) / Add New Target (Manager)
 */
const OVERLAP_MSG = 'There are other slots during this time, please modify or delete the original slot(s) to add a new slot.';

const AddNewSlotDialog = ({
  open,
  onClose,
  date,
  isManagerMode,
  onSave,
  instructorId,
  managerId,
  token,
  prefillTimes,
  existingSlots = [],
}) => {
  const [startTime, setStartTime] = useState(() => (prefillTimes?.start ?? ''));
  const [endTime, setEndTime] = useState(() => (prefillTimes?.end ?? ''));
  const [recurring, setRecurring] = useState(false);
  const [rrule, setRrule] = useState('');
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');
  const [timeOrderError, setTimeOrderError] = useState('');
  const [overlapError, setOverlapError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setStartTime('');
      setEndTime('');
      setRecurring(false);
      setRrule('');
      setStartError('');
      setEndError('');
      setTimeOrderError('');
      setOverlapError('');
    } else {
      if (prefillTimes) {
        setStartTime(prefillTimes.start || '');
        setEndTime(prefillTimes.end || '');
      } else {
        setStartTime('');
        setEndTime('');
      }
    }
  }, [open, prefillTimes]);

  useEffect(() => {
    if (!open || !startTime.trim() || !endTime.trim()) return;
    const sm = parseHHMM(startTime);
    const em = parseHHMM(endTime);
    if (sm == null || em == null || !Array.isArray(existingSlots) || existingSlots.length === 0) {
      setOverlapError('');
      return;
    }
    const overlaps = existingSlots.some((slot) => sm < slot.endMinutes && em > slot.startMinutes);
    setOverlapError(overlaps ? OVERLAP_MSG : '');
  }, [open, existingSlots, startTime, endTime]);

  const validateStart = (val) => {
    if (!val || !val.trim()) return '';
    if (!HHMM_REGEX.test(val.trim())) return 'Must be HH:MM format';
    return '';
  };

  const validateEnd = (val) => {
    if (!val || !val.trim()) return '';
    if (!HHMM_REGEX.test(val.trim())) return 'Must be HH:MM format';
    return '';
  };

  const validateOrder = (s, e) => {
    const sm = parseHHMM(s);
    const em = parseHHMM(e);
    if (sm == null || em == null) return '';
    if (sm >= em) return 'Start time must be before end time';
    return '';
  };

  const checkOverlap = (s, e) => {
    const sm = parseHHMM(s);
    const em = parseHHMM(e);
    if (sm == null || em == null || !Array.isArray(existingSlots) || existingSlots.length === 0) return '';
    const overlaps = existingSlots.some(
      (slot) => sm < slot.endMinutes && em > slot.startMinutes
    );
    return overlaps ? OVERLAP_MSG : '';
  };

  const handleStartBlur = () => {
    setStartError(validateStart(startTime));
    setTimeOrderError(validateOrder(startTime, endTime));
    setOverlapError(checkOverlap(startTime, endTime));
  };

  const handleEndBlur = () => {
    setEndError(validateEnd(endTime));
    setTimeOrderError(validateOrder(startTime, endTime));
    setOverlapError(checkOverlap(startTime, endTime));
  };

  const handleStartChange = (e) => {
    const v = e.target.value;
    setStartTime(v);
    setStartError(validateStart(v));
    setTimeOrderError(validateOrder(v, endTime));
    setOverlapError(checkOverlap(v, endTime));
  };

  const handleEndChange = (e) => {
    const v = e.target.value;
    setEndTime(v);
    setEndError(validateEnd(v));
    setTimeOrderError(validateOrder(startTime, v));
    setOverlapError(checkOverlap(startTime, v));
  };

  const startValid = !startTime.trim() || !validateStart(startTime);
  const endValid = !endTime.trim() || !validateEnd(endTime);
  const orderValid = !timeOrderError;
  const noOverlap = !overlapError;
  const recurringValid = !recurring || (recurring && rrule);
  const canSave =
    startTime.trim() &&
    endTime.trim() &&
    startValid &&
    endValid &&
    orderValid &&
    noOverlap &&
    recurringValid &&
    !saving;

  const handleSave = async () => {
    if (!canSave || !date) return;
    const dateStr = typeof date === 'string' ? date.slice(0, 10) : date.toISOString?.().slice(0, 10);
    const baseURL = process.env.NODE_ENV === 'production' ? 'https://allcprmanage.com' : 'http://localhost:8080';

    const payload = {
      date: dateStr,
      startTime: startTime.trim().length <= 5 ? `${startTime.trim()}:00` : startTime.trim(),
      endTime: endTime.trim().length <= 5 ? `${endTime.trim()}:00` : endTime.trim(),
      isRecurring: recurring,
      rrule: recurring ? rrule : null,
    };

    setSaving(true);
    try {
      if (isManagerMode) {
        await axiosInstance.post('/proposal', {
          ...payload,
          instructorId,
          managerId,
        });
      } else {
        await axios.post(`${baseURL}/proposal/public`, payload, {
          params: { token },
        });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Create proposal failed:', err);
      alert(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const title = isManagerMode ? 'Add New Target' : 'Add New Availability';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TimeInput
          fullWidth
          label="Start Time"
          placeholder="HHMM"
          value={startTime}
          onChange={handleStartChange}
          onBlur={handleStartBlur}
          error={!!startError}
          helperText={startError}
          margin="normal"
        />
        <TimeInput
          fullWidth
          label="End Time"
          placeholder="HHMM"
          value={endTime}
          onChange={handleEndChange}
          onBlur={handleEndBlur}
          error={!!endError}
          helperText={endError}
          margin="normal"
        />
        {timeOrderError && (
          <FormHelperText error sx={{ mt: -1, mb: 1 }}>
            {timeOrderError}
          </FormHelperText>
        )}
        {overlapError && (
          <FormHelperText error sx={{ mt: 1, mb: 1 }}>
            {overlapError}
          </FormHelperText>
        )}
        <FormControlLabel
          control={
            <Checkbox checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
          }
          label="Recurring"
          sx={{ mt: 2, display: 'block' }}
        />
        {recurring && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Recurrence</InputLabel>
            <Select
              value={rrule}
              label="Recurrence"
              onChange={(e) => setRrule(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="biweekly">Biweekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewSlotDialog;
